import React from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { Helmet } from 'react-helmet'
import styled from 'styled-components'
import moment from 'moment'
import getFromObject from 'lodash/get'
import startCase from 'lodash/startCase'
import { FormattedMessage, injectIntl } from 'react-intl'

import Popper from '@material-ui/core/Popper'
import Fade from '@material-ui/core/Fade'
import Paper from '@material-ui/core/Paper'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'

import { Colors } from 'tr-components/Colors'
import { fetchAsset, reserveAssetKey } from '../../redux/reducers/marketingAssetsCollection'
import { getAssetsFile } from '../../static/marketingAssetsServices'
import { fetchLocations } from '../../redux/reducers/locations'

import AmplifyService from '../../services/amplify'
import { getPostingIdentifier, getPostingUuid } from '../../services/postingIdentifier'
import ConsentManager from '../../utils/consentManager'
import ExtendedApplyModal from '../modals/ExtendedApplyModal'
import ShareButtons from './ShareButtons'
import { originUrl, ATSApplyURL, applyUrl } from '../../config/endpoints'
import parse from 'html-react-parser'
const formatCompensation = (amount) => {
  if (!amount) return ''

  const numAmount = Number(amount)
  if (isNaN(numAmount)) return ''

  return `$${numAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

const formatCompensationShort = (amount) => {
  if (!amount) return ''

  const numAmount = Number(amount)
  if (isNaN(numAmount)) return ''

  if (numAmount >= 1000) {
    const thousands = numAmount / 1000
    if (thousands === Math.floor(thousands)) {
      return `$${thousands}k`
    }
    if (thousands === Math.floor(thousands * 10) / 10) {
      return `$${thousands.toFixed(1)}k`
    }
  }

  return `$${numAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

const StyledTitle = styled.h1`
  padding: 0;
  font-family: ${(font) => font.fontFamily};
  font-size: ${(font) => (font.size ? `${font.size}px` : '1rem')};
  color: ${(font) => (font.color ? font.color : Colors.lightGray)};
  font-weight: normal;
  text-align: left;
  text-transform: uppercase;
  margin: 0;
`

const Article = styled.article`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  padding: 0 20px 50px;
  max-width: 980px;
  margin: 0 auto;

  & > div:first-child {
    width: 60%;
  }
  & > div:last-child {
    width: 40%;
    padding-left: 20px;
    box-sizing: border-box;
    float: left;
  }
  @media screen and (max-width: 780px) {
    flex-direction: column;
    & > div:first-child,
    & > div:last-child {
      width: 100%;
      padding: 0;
    }
    .not-mobile {
      display: none;
    }
  }
`

const FontStyled = styled.div`
  font-family: ${(font) => font.fontFamily}, sans-serif;
  font-size: ${(font) => (font.size ? `${font.size}px` : 'inherit')};
  color: ${(font) => font.color};
`

const MobileOnly = styled.div`
  @media screen and (min-width: 780px) {
    display: none;
  }
`

const SectionBackground = styled.div`
  margin: 0 auto;
  width: 100%;
  padding: 20px 0;
  background: ${(props) => props.color || 'white'};
`

const Header = styled.header`
  max-width: 980px;
  padding: 10px 20px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
`

const Button = styled.button`
  padding: 8px 14px;
  text-transform: uppercase;
  font-size: 16px;
  background: ${(props) => props.color || Colors.lightGray};
  display: inline-block;
  color: white;
  border: none;
  border-radius: 3px;
  font-weight: bold;
  cursor: pointer;
  @media screen and (min-width: 780px) {
    padding: 10px 20px;
  }
`
const ButtonsContainer = styled.div`
  @media screen and (max-width: 780px) {
    display: none;
  }
`
const AdditionalInfo = styled.div`
  padding: 0;
  padding-left: 20px;
  text-align: right;
  @media screen and (max-width: 780px) {
    text-align: left;
  }
`
const AdditionalInfoTitle = styled.h4`
  @media screen and (min-width: 780px) {
    margin-top: 0;
  }
`
const JobDescription = styled.div`
  /* Only force inheritance when parent FontStyled has template styling configured */
  ${(props) =>
    (props.hasTemplateColor || props.hasTemplateFontSize) &&
    `
    * {
      ${props.hasTemplateColor ? 'color: inherit !important;' : ''}
      ${props.hasTemplateFontSize ? 'font-size: inherit !important;' : ''}
    }
  `};

  @media screen and (max-width: 780px) {
    iframe,
    img {
      width: 100%;
    }
  }
  @media screen and (min-width: 780px) {
    & :first-child {
      margin-top: 0;
    }
  }
`

const SharingPopper = styled(Popper)`
  &:after {
    content: '';
    position: absolute;
    top: -10px;
    transform: translateX(-50%);
    left: 50%;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 0 10px 10px 10px;
    border-color: transparent transparent #fff transparent;
  }
`

const SharingContainer = styled(Paper)`
  padding: 5px 10px;
`

const SharingTitle = styled.div`
  margin-bottom: 5px;
`
const CompensationTag = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 2px 8px;
  gap: 8px;
  height: 28px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  margin-top: 10px;
  margin-bottom: 10px;
  background-color: ${(props) => props.backgroundColor};
  color: ${(props) => props.color};
  font-family: ${(props) => props.fontFamily};
`

const MoneyIcon = ({ color }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_money_icon)">
      <path
        d="M23.2935 4.44186C21.8389 3.83173 20.3831 3.59961 18.9281 3.59961C14.3093 3.59923 9.69075 5.93736 5.07188 5.93736C3.9135 5.93736 2.75588 5.79036 1.5975 5.42286C1.46738 5.38161 1.33688 5.36211 1.20938 5.36211C0.564 5.36211 0 5.86161 0 6.55498V18.4522C0 18.9259 0.271125 19.3747 0.7065 19.557C2.16113 20.1675 3.61688 20.3996 5.07188 20.3996C9.69075 20.3996 14.3096 18.0615 18.9285 18.0615C20.0869 18.0615 21.2445 18.2085 22.4029 18.576C22.533 18.6172 22.6635 18.6367 22.791 18.6367C23.4364 18.6367 24.0004 18.1372 24.0004 17.4439V5.54698C24 5.07298 23.7289 4.62448 23.2935 4.44186ZM1.8 7.35786C2.5545 7.54686 3.342 7.64173 4.152 7.69273C3.9315 8.79486 2.9625 9.62548 1.8 9.62548V7.35786ZM1.8 18.0454V16.2536C3.08888 16.2536 4.13175 17.2762 4.18913 18.5561C3.34425 18.4882 2.5545 18.3195 1.8 18.0454ZM12 15.5996C10.3429 15.5996 9 13.9875 9 11.9996C9 10.0114 10.3433 8.39961 12 8.39961C13.6568 8.39961 15 10.0114 15 11.9996C15 13.9882 13.6564 15.5996 12 15.5996ZM22.2 16.6414C21.543 16.4767 20.8609 16.3845 20.163 16.3249C20.3831 15.3469 21.1943 14.6044 22.2 14.4769V16.6414ZM22.2 7.78723C21.0416 7.64061 20.1428 6.67348 20.1071 5.47911C20.8399 5.56048 21.5355 5.71273 22.2 5.95386V7.78723Z"
        fill={color || 'rgba(0, 0, 0, 0.6)'}
      />
    </g>
    <defs>
      <clipPath id="clip0_money_icon">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
)

const facebook_pixel_id = process.env.REACT_APP_FACEBOOK_PIXEL_ID
const facebook_pixel_tracking_enabled = process.env.REACT_APP_FACEBOOK_PIXEL_TRACKING_ENABLED

export class PositionInformation extends React.Component {
  state = {
    showApplyModal: false,
    applyLink: null,
    anchorEl: null,
    isSharingPopperOpened: false,
    shortenedUrl: {
      email: '',
      sms: '',
    },
    isApplyModalExtended: false,
  }

  componentDidMount() {
    this.loadGatedTracking()
    // Load the gated marketing/analytics scripts in the background when consent
    // is granted, without a full page reload.
    window.addEventListener('consentUpdated', this.handleConsentUpdated)
  }

  componentDidUpdate(prevProps) {
    // The consent-banner flag loads asynchronously after mount. Re-run tracking
    // once it resolves so the marketing/analytics scripts load or stay blocked
    // correctly — mirroring the reactive GTM tags in App.
    if (
      prevProps.featureFlags?.NewCookieConsentBanner !==
      this.props.featureFlags?.NewCookieConsentBanner
    ) {
      this.loadGatedTracking()
    }
  }

  componentWillUnmount() {
    window.removeEventListener('consentUpdated', this.handleConsentUpdated)
  }

  handleConsentUpdated = () => {
    this.loadGatedTracking()
  }

  loadGatedTracking() {
    const {
      posting: { brand, brandId, clientId, clientName, department, jobId, location, positionId },
    } = this.props

    // Until the consent-banner flag resolves we cannot know whether consent is
    // required, so don't load anything yet — componentDidUpdate re-runs this
    // once the flag resolves (and after the user consents, which reloads).
    const consentBannerEnabled = this.props.featureFlags?.NewCookieConsentBanner
    if (consentBannerEnabled === undefined) {
      return
    }

    const referrer = document.referrer
    const utmSource =
      AmplifyService.extractUtmSource(window.location.search) ||
      AmplifyService.extractSourceFromReferrer(referrer)

    // Flag on: require marketing consent for the Facebook pixel. Flag off: load as-is.
    if (
      !this.fbPixelLoaded &&
      utmSource === 'facebook' &&
      facebook_pixel_tracking_enabled === 'true' &&
      (!consentBannerEnabled || ConsentManager.hasMarketingConsent(clientId))
    ) {
      this.fbPixelLoaded = true
      // eslint-disable-next-line
      !(function (f, b, e, v, n, t, s) {
        if (f.fbq) return
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
        }
        if (!f._fbq) f._fbq = n
        n.push = n
        n.loaded = !0
        n.version = '2.0'
        n.queue = []
        t = b.createElement(e)
        t.async = !0
        t.src = v
        s = b.getElementsByTagName(e)[0]
        s.parentNode.insertBefore(t, s)
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')
      window.fbq('init', facebook_pixel_id)
      window.fbq('track', 'PageView')
    }

    // This try/catch is to don't stop the loading of Job Description Page
    // when it was launched in preview mode and does not have the whole posting information.
    // Flag on: require analytics consent before emitting Source Tracking. Flag off: load as-is.
    if (
      !this.amplifySent &&
      (!consentBannerEnabled || ConsentManager.hasAnalyticsConsent(clientId))
    ) {
      this.amplifySent = true
      try {
        AmplifyService.initialize()

        AmplifyService.sendViewedEvent({
          brandId,
          brandName: brand,
          clientId,
          clientName,
          departmentId: department.id,
          departmentName: department.name,
          locationId: location.id,
          locationName: location.name,
          locationNumber: location.number,
          positionId,
          postingId: jobId,
          referrer,
          utmSource,
        })
      } catch (e) {
        console.warn(e)
      }
    }
  }

  getAsset = (assetPath, details = true) => {
    const { assetsCollection, fetchAsset, clientId, reserveAssetKey, styles } = this.props

    let assetId = ''
    const asset = getFromObject(styles, assetPath, null)
    if (asset && asset.id) {
      return details ? asset.detail[asset.assetType] : asset
    } else {
      assetId = getFromObject(styles, assetPath + 'MaId', null)
    }

    if (assetId && assetsCollection && assetsCollection[assetId]) {
      return assetsCollection[assetId]
    } else if (assetId) {
      reserveAssetKey(assetId)
      fetchAsset(clientId, assetId)
      return {}
    }
    return {}
  }

  removeInternalApplicantParam = (urlString) => {
    if (!urlString) return urlString
    return urlString
      .replace(/([?&])internalApplicant=[^&]*/gi, '$1')
      .replace(/[?&]$/, '')
      .replace(/\?&/, '?')
  }

  showApplyModal = () => {
    const { posting, availability, landedPostingUuid, analyticsParameters } = this.props
    const { url } = posting
    const { isApplyModalExtended } = this.state
    // Strip internalApplicant param when UUID-based URL is used
    const cleanUrl = getPostingUuid(posting, availability, landedPostingUuid)
      ? this.removeInternalApplicantParam(url)
      : url
    if (!isApplyModalExtended) {
      const ATSUrl = `${ATSApplyURL + cleanUrl}${analyticsParameters}`
      const applicationUrl = this.getModernApplyUrl(ATSUrl)
      window.open(applicationUrl)
    } else {
      this.setState({
        showApplyModal: true,
        applyLink: ATSApplyURL + cleanUrl,
      })
    }
  }

  closeApplyModal = () => {
    this.setState({
      showApplyModal: false,
    })
  }

  convertStatus(status) {
    const mappedStatuses = { temporarySeasonal: 'Temporary / Seasonal' }
    const convertedStatus = mappedStatuses[status] || startCase(status)

    return convertedStatus
  }

  handleSharingPopper = (event) => {
    this.setState({
      anchorEl: this.state.anchorEl ? null : event.currentTarget,
      isSharingPopperOpened: !this.state.isSharingPopperOpened,
      shortenedUrl: {
        email: this.getApplyUrl('email_share', 'email'),
        sms: this.getApplyUrl('sms_share', 'sms'),
      },
    })
  }

  handleClickAway = () => {
    this.setState({
      anchorEl: null,
      isSharingPopperOpened: false,
    })
  }

  getApplyUrl = (utm_source, utm_medium) => {
    const { clientId } = this.props.posting
    const { locale, availability, landedPostingUuid } = this.props
    const postingIdentifier = getPostingIdentifier(
      this.props.posting,
      availability,
      landedPostingUuid
    )
    return encodeURIComponent(
      `${originUrl}/clients/${clientId}/posting/${postingIdentifier}/${locale}?utm_source=${utm_source}&utm_medium=${utm_medium}`
    )
  }

  getModernApplyUrl = (redirectUrl) => {
    const postingIdentifier = getPostingIdentifier(
      this.props.posting,
      this.props.availability,
      this.props.landedPostingUuid
    )
    return `${applyUrl}/apply/${postingIdentifier}?redirect=${encodeURIComponent(redirectUrl)}`
  }

  postingStatusToGoogleEmployment = (status) => {
    switch (status) {
      case 'fullTime':
        return 'FULL_TIME'
      case 'partTime':
        return 'PART_TIME'
      case 'seasonal':
        return 'temporary'
      case 'contract':
        return 'CONTRACT'
      case 'internship':
        return 'INTERNSHIP'
      case 'volunteer':
        return 'VOLUNTEER'
      default:
        return ''
    }
  }

  postingShiftsToWorkHours = (shifts) => {
    const { intl } = this.props

    const shiftsArray = []
    if (shifts.firstShift) {
      shiftsArray.push(intl.formatMessage({ id: 'positionInfo.first' }))
    }
    if (shifts.secondShift) {
      shiftsArray.push(intl.formatMessage({ id: 'positionInfo.second' }))
    }
    if (shifts.thirdShift) {
      shiftsArray.push(intl.formatMessage({ id: 'positionInfo.third' }))
    }

    return shiftsArray.join(', ')
  }

  buildGoogleStructuredData = (posting) => {
    const { intl } = this.props

    const structuredData = {
      '@context': 'http://schema.org/',
      '@type': 'JobPosting',
      title: posting.positionType,
      description: posting.description,
      identifier: {
        '@type': 'PropertyValue',
        name: posting.clientName,
        value: posting.jobId,
      },

      datePosted: posting.createdDate,
      validThrough: posting.endDate,

      image: getAssetsFile(posting.clientId, this.getAsset('header.logo').fileId),

      hiringOrganization: {
        '@type': 'Organization',
        name: posting.brand,
      },

      jobLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          streetAddress: posting.address.street1,
          addressLocality: posting.address.city,
          addressRegion: posting.address.stateOrProvince,
          postalCode: posting.address.postalCode,
          addressCountry: posting.address.country,
        },
      },

      workHours: posting.isSalaried
        ? intl.formatMessage({ id: 'positionInfo.salaried' })
        : this.postingShiftsToWorkHours(posting.shifts),
    }

    const employmentType = this.postingStatusToGoogleEmployment(posting.contractType)
    if (employmentType) {
      structuredData.employmentType = employmentType
    }

    /**
     * Omitted data from Google Structured jobs:
     * Image urls may need to be ported somewhere usable
     * Salary information can be added in the future using minCompensation/maxCompensation
     * Valid license and reliable transportation not in ES
     * Education requirement not in ES
     */

    return structuredData
  }

  isYearlyCompensation(posting) {
    return posting?.maxCompensationType === 'annual'
  }

  render() {
    const { applyLink, showApplyModal, shortenedUrl, isApplyModalExtended } = this.state
    const {
      analyticsParameters,
      id,
      posting,
      availability,
      landedPostingUuid,
      clientId,
      templateType,
      templateId,
      locale,
      styles: {
        fontTitle,
        position: {
          buttonShare,
          buttonApply,
          fontDescription,
          fontAdditionalInfoTitle,
          additionalInfoTitle,
          fontAdditionalInfo,
          additionalInfoFields,
        },
      },
      intl,
    } = this.props

    const Buttons = (
      <div id="position-info-buttons">
        <Button
          id="position-info-button-apply"
          onClick={this.showApplyModal}
          color={this.getAsset('position.buttonApply.backgroundColor').hex}
        >
          <FontStyled
            id="position-info-button-apply-font"
            fontFamily={this.getAsset('position.buttonApply.font.fontFamily', false).name}
            size={buttonApply && buttonApply.font && buttonApply.font.fontSize}
            color={this.getAsset('position.buttonApply.font.fontColor').hex}
          >
            <FormattedMessage id="careerSearch.applyButton" defaultMessage="Apply" />
          </FontStyled>
        </Button>
        <Button
          id="position-info-button-share"
          style={{ marginLeft: '20px' }}
          color={this.getAsset('position.buttonShare.backgroundColor').hex}
          target="_blank"
          onClick={this.handleSharingPopper}
        >
          <FontStyled
            id="position-info-button-share-font"
            fontFamily={this.getAsset('position.buttonShare.font.fontFamily', false).name}
            size={buttonShare && buttonShare.font && buttonShare.font.fontSize}
            color={this.getAsset('position.buttonShare.font.fontColor').hex}
          >
            <FormattedMessage id="general.share" defaultMessage="Share" />
          </FontStyled>
        </Button>
        <SharingPopper
          open={this.state.isSharingPopperOpened}
          anchorEl={this.state.anchorEl}
          transition
        >
          {({ TransitionProps }) => (
            <ClickAwayListener onClickAway={this.handleClickAway}>
              <Fade {...TransitionProps} timeout={100}>
                <SharingContainer>
                  <SharingTitle>
                    <FontStyled
                      fontFamily={this.getAsset('position.buttonShare.font.fontFamily', false).name}
                      size={buttonShare && buttonShare.font && buttonShare.font.fontSize}
                      color={this.getAsset('position.buttonShare.font.fontColorMaId').hex}
                    >
                      <FormattedMessage id="share.job" defaultMessage="Share job" />
                    </FontStyled>
                  </SharingTitle>
                  <ShareButtons
                    clientName={posting.clientName}
                    clientId={posting.clientId}
                    jobId={posting.jobId}
                    postingUuid={getPostingUuid(posting, availability, landedPostingUuid)}
                    locale={locale}
                    positionType={posting.positionType}
                    positionLocation={`${posting.address.street1}, ${posting.address.city}, ${posting.address.country}, ${posting.address.stateOrProvince}, ${posting.address.postalCode}`}
                    templateId={templateId}
                    templateType={templateType}
                    shortenedUrl={shortenedUrl}
                  />
                </SharingContainer>
              </Fade>
            </ClickAwayListener>
          )}
        </SharingPopper>
      </div>
    )

    return (
      <SectionBackground id={id} color={this.getAsset('backgroundColor').hex}>
        <Helmet>
          {posting && (
            <script type="application/ld+json">
              {JSON.stringify(this.buildGoogleStructuredData(posting))}
            </script>
          )}
        </Helmet>
        <Header id="position-info-header">
          <div>
            <StyledTitle
              id="position-info-title"
              fontFamily={this.getAsset('fontTitle.fontFamily', false).name}
              size={fontTitle && fontTitle.fontSize}
              color={this.getAsset('fontTitle.fontColor').hex}
            >
              <span>{posting.positionType}</span> <br />
              <span style={{ fontSize: fontTitle ? fontTitle.fontSize - 4 : '' }}>
                {posting.address.city}, {posting.address.stateOrProvince}
              </span>
            </StyledTitle>
          </div>
          <ButtonsContainer>{Buttons}</ButtonsContainer>
        </Header>
        <Article>
          <div style={{ paddingBottom: '10px' }}>
            <div id="position-info-mobile-buttons-container">
              <MobileOnly>{Buttons}</MobileOnly>
            </div>
            {posting.pubCompensation === 1 &&
              posting.minCompensation &&
              posting.maxCompensation && (
                <div style={{ width: 'fit-content', paddingLeft: '0', margin: '0' }}>
                  <CompensationTag
                    backgroundColor={
                      this.getAsset('position.fontDescription.fontColor').hex
                        ? `${this.getAsset('position.fontDescription.fontColor').hex}1F`
                        : 'rgba(0, 0, 0, 0.12)'
                    }
                    color={this.getAsset('position.fontDescription.fontColor').hex || '#666'}
                    fontFamily={this.getAsset('position.fontDescription.fontFamily', false).name}
                  >
                    <MoneyIcon
                      color={
                        this.getAsset('position.fontDescription.fontColor').hex ||
                        'rgba(0, 0, 0, 0.6)'
                      }
                    />
                    <span>
                      {formatCompensationShort(posting.minCompensation)} -{' '}
                      {formatCompensationShort(posting.maxCompensation)}{' '}
                      <FormattedMessage
                        id={
                          this.isYearlyCompensation(posting)
                            ? 'positionInfo.perYear'
                            : 'positionInfo.perHour'
                        }
                      />
                    </span>
                  </CompensationTag>
                </div>
              )}
            <FontStyled
              id="position-info-posting-description"
              fontFamily={this.getAsset('position.fontDescription.fontFamily', false).name}
              size={fontDescription && fontDescription.fontSize}
              color={this.getAsset('position.fontDescription.fontColor').hex}
            >
              <JobDescription
                className="ql-editor"
                hasTemplateColor={!!this.getAsset('position.fontDescription.fontColor').hex}
                hasTemplateFontSize={!!(fontDescription && fontDescription.fontSize)}
              >
                {parse(posting.description)}
              </JobDescription>
            </FontStyled>
            <br />
            <FontStyled
              id="position-info-already-applied"
              fontFamily={this.getAsset('position.fontDescription.fontFamily', false).name}
              size={fontDescription && fontDescription.fontSize}
              color={this.getAsset('position.fontDescription.fontColor').hex}
            >
              <a href={applyUrl + '/applicant/recover-applications'}>
                {intl.formatMessage({ id: 'apply.alreadyApplied' })}
              </a>
            </FontStyled>
          </div>
          <MobileOnly>{Buttons}</MobileOnly>
          {additionalInfoFields && (
            <AdditionalInfo id="position-info-additional-info">
              <AdditionalInfoTitle id="position-info-additional-info-title">
                <FontStyled
                  id="position-info-additional-info-title-font"
                  fontFamily={
                    this.getAsset('position.fontAdditionalInfoTitle.fontFamily', false).name
                  }
                  size={fontAdditionalInfoTitle && fontAdditionalInfoTitle.fontSize}
                  color={this.getAsset('position.fontAdditionalInfoTitle.fontColor').hex}
                >
                  {additionalInfoTitle || intl.formatMessage({ id: 'positionInfo.positionInfo' })}
                </FontStyled>
              </AdditionalInfoTitle>
              <div id="position-info-additional-info-fields">
                <FontStyled
                  id="position-info-additional-info-fields-font"
                  fontFamily={this.getAsset('position.fontAdditionalInfo.fontFamily', false).name}
                  size={fontAdditionalInfo && fontAdditionalInfo.fontSize}
                  color={this.getAsset('position.fontAdditionalInfo.fontColor').hex}
                >
                  {additionalInfoFields.includes('Company') && (
                    <p className="not-mobile">
                      <FormattedMessage id="positionInfo.company" defaultMessage="Company" />:{' '}
                      {posting.clientName}
                    </p>
                  )}
                  {additionalInfoFields.includes('Position') && (
                    <p className="not-mobile">
                      <FormattedMessage id="positionInfo.position" defaultMessage="Position" />:{' '}
                      {posting.positionType}
                    </p>
                  )}
                  {additionalInfoFields.includes('Status') && (
                    <p>
                      <FormattedMessage id="positionInfo.status" defaultMessage="Status" />:{' '}
                      {this.convertStatus(posting.contractType)}
                    </p>
                  )}
                  {posting.pubCompensation === 1 &&
                    posting.minCompensation &&
                    posting.maxCompensation && (
                      <p>
                        <FormattedMessage
                          id={
                            this.isYearlyCompensation(posting)
                              ? 'positionInfo.baseSalaryRange'
                              : 'positionInfo.payRange'
                          }
                        />
                        : {formatCompensation(posting.minCompensation)} -{' '}
                        {formatCompensation(posting.maxCompensation)}
                        <FormattedMessage
                          id={
                            this.isYearlyCompensation(posting)
                              ? 'positionInfo.yearSuffix'
                              : 'positionInfo.hourSuffix'
                          }
                        />
                      </p>
                    )}
                  {additionalInfoFields.includes('Shift') && (
                    <p>
                      <FormattedMessage id="positionInfo.shift" defaultMessage="Shift" />:{' '}
                      {posting.shifts && (
                        <span>{this.postingShiftsToWorkHours(posting.shifts)}</span>
                      )}
                    </p>
                  )}
                  {additionalInfoFields.includes('Req #') && (
                    <p className="not-mobile">
                      <FormattedMessage id="positionInfo.req" defaultMessage="Req #" />:{' '}
                      {posting.jobId}
                    </p>
                  )}
                  {additionalInfoFields.includes('Date Posted') && (
                    <p className="not-mobile">
                      <FormattedMessage id="positionInfo.date" defaultMessage="Date Posted" />:{' '}
                      {moment(posting.createdDate, 'YYYY-MM-DD').format('MMMM DD, YYYY')}
                    </p>
                  )}
                  {additionalInfoFields.includes('Location') && posting.address && (
                    <p>
                      <FormattedMessage id="positionInfo.location" defaultMessage="Location" />:{' '}
                      {`${posting.address.street1}, ${posting.address.city},
                    ${posting.address.country},
                    ${posting.address.stateOrProvince},
                    ${posting.address.postalCode}
                  `}
                    </p>
                  )}
                  {additionalInfoFields.includes('Job Category') && (
                    <p className="not-mobile">
                      <FormattedMessage id="positionInfo.category" defaultMessage="Job Category" />:{' '}
                      {posting.category}
                    </p>
                  )}
                </FontStyled>
              </div>
            </AdditionalInfo>
          )}
        </Article>
        {showApplyModal && isApplyModalExtended && (
          <ExtendedApplyModal
            id="position-info-apply-modal"
            open={showApplyModal}
            clientId={clientId}
            templateId={templateId}
            templateType={templateType}
            analyticsParameters={analyticsParameters}
            locale={locale}
            jobInfo={posting}
            URL={applyLink}
            availability={availability}
            onCancel={this.closeApplyModal}
            color={this.getAsset('position.buttonApply.backgroundColor').hex}
            size={buttonApply && buttonApply.font && buttonApply.font.fontSize}
            fontFamily={this.getAsset('position.buttonApply.font.fontFamily', false).name}
            fontColor={this.getAsset('position.buttonApply.font.fontColorMaId').hex}
            newCookieConsentBannerEnabled={this.props.featureFlags?.NewCookieConsentBanner}
          />
        )}
      </SectionBackground>
    )
  }
}

const mapStateToProps = ({
  assetsCollection,
  template,
  jobPosting: { posting },
  user,
  featureFlags,
}) => ({
  assetsCollection: assetsCollection.assets,
  templateType: template.type,
  posting,
  analyticsParameters: user.analyticsParameters,
  featureFlags: featureFlags.featureFlags,
})

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchAsset,
      fetchLocations,
      reserveAssetKey,
    },
    dispatch
  )

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(PositionInformation))
